import { useCallback, useState, useRef } from 'react';
import { notifications } from '@good/ui/notifications';
import apiClient from 'utilities/api-client';

interface JobStatus {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  error?: string;
}

interface UseGroupScheduleProgressOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
}

export function useGroupScheduleProgress(options: UseGroupScheduleProgressOptions = {}) {
  const [activeJobs, setActiveJobs] = useState<Map<string, JobStatus>>(new Map());
  const notificationIdsRef = useRef<Map<string, string>>(new Map());

  const showProgressNotification = useCallback((jobId: string, initialMessage: string) => {
    const notificationId = `group-schedule-${jobId}`;

    notifications.show({
      id: notificationId,
      title: 'Processing Group Schedule',
      message: initialMessage,
      color: 'blue',
      autoClose: false,
      withCloseButton: false,
    });

    notificationIdsRef.current.set(jobId, notificationId);
    setActiveJobs((prev) =>
      new Map(prev).set(jobId, {
        jobId,
        status: 'queued',
        progress: 0,
      }),
    );
  }, []);

  const updateProgress = useCallback((jobId: string, progress: number, message?: string) => {
    const notificationId = notificationIdsRef.current.get(jobId);
    if (!notificationId) return;

    setActiveJobs((prev) => {
      const newMap = new Map(prev);
      const currentJob = newMap.get(jobId);
      if (currentJob) {
        newMap.set(jobId, { ...currentJob, progress, message });
      }
      return newMap;
    });

    notifications.update({
      id: notificationId,
      title: 'Processing Group Schedule',
      message: message || `Processing your request... (${progress}%)`,
      color: 'blue',
      autoClose: false,
      withCloseButton: false,
    });
  }, []);

  const completeJob = useCallback(
    (jobId: string, success: boolean, message?: string, error?: string) => {
      const notificationId = notificationIdsRef.current.get(jobId);
      if (!notificationId) {
        return;
      }

      if (success) {
        notifications.update({
          id: notificationId,
          title: 'Group Schedule Completed',
          message: message || 'Your group schedule has been processed successfully!',
          color: 'green',
          autoClose: 3000,
          withCloseButton: false,
        });

        options.onSuccess?.();
      } else {
        notifications.update({
          id: notificationId,
          title: 'Group Schedule Failed',
          message: message || 'There was an error processing your group schedule.',
          color: 'red',
          autoClose: 3000,
          withCloseButton: false,
        });

        options.onError?.(error || 'Unknown error occurred');
      }

      // Clean up
      setActiveJobs((prev) => {
        const newMap = new Map(prev);
        newMap.delete(jobId);
        return newMap;
      });

      notificationIdsRef.current.delete(jobId);

      options.onComplete?.();
    },
    [options],
  );

  const pollJobStatus = useCallback(
    async (jobId: string, retryCount = 0) => {
      try {
        const response = await apiClient.get(`/api/portal/group-services/job-status/${jobId}`);
        const jobStatus = response.data;

        // Check for the correct status values from the database
        if (jobStatus.status === 'Completed') {
          completeJob(jobId, true, 'Group schedule processed successfully!');
        } else if (jobStatus.status === 'Failed') {
          completeJob(jobId, false, 'Failed to process group schedule', jobStatus.error);
        } else if (jobStatus.status === 'Active') {
          // Estimate progress based on time elapsed or use provided progress
          const progress = jobStatus.progress || 50; // Default to 50% when processing
          updateProgress(jobId, progress, 'Processing your group schedule...');

          // Continue polling
          setTimeout(() => pollJobStatus(jobId, 0), 2000);
        } else {
          // Still queued, continue polling
          setTimeout(() => pollJobStatus(jobId, 0), 3000);
        }
      } catch (error: any) {
        // Handle 404 errors with retry logic
        if (error?.response?.status === 404 && retryCount < 5) {
          setTimeout(() => pollJobStatus(jobId, retryCount + 1), 2000);
          return;
        }

        // For other errors or max retries reached, complete with error
        completeJob(jobId, false, 'Failed to check job status', 'Network error');
      }
    },
    [completeJob, updateProgress],
  );

  const startProgressTracking = useCallback(
    (jobId: string, initialMessage: string) => {
      showProgressNotification(jobId, initialMessage);
      // Add a small delay to allow the job record to be created in the database
      setTimeout(() => pollJobStatus(jobId), 1000);
    },
    [showProgressNotification, pollJobStatus],
  );

  return {
    startProgressTracking,
    updateProgress,
    completeJob,
    activeJobs: Array.from(activeJobs.values()),
  };
}
